import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Copy, Fingerprint } from "lucide-react";

const STATUS_BADGE = {
  received: "bg-blue-50 text-blue-800 border-blue-200",
  under_review: "bg-amber-50 text-amber-900 border-amber-200",
  verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
  duplicate: "bg-slate-100 text-slate-700 border-slate-300",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function Declarations() {
  const [decls, setDecls] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [filters, setFilters] = useState({ status: "all", dupOnly: false, includeTest: true });
  const [selected, setSelected] = useState(null);
  const [review, setReview] = useState({ status: "verified", note: "", duplicate_of: "" });
  const [sigUrl, setSigUrl] = useState(null);
  const [hashCheck, setHashCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = { include_test: filters.includeTest, duplicates_only: filters.dupOnly };
    if (filters.status !== "all") params.status = filters.status;
    adminApi.get("/admin/declarations", { params }).then((r) => setDecls(r.data)).catch(() => {});
    adminApi.get("/admin/incidents").then((r) => setIncidents(r.data)).catch(() => {});
  }, [filters]);
  useEffect(load, [load]);

  const openDetail = async (d) => {
    setSelected(d);
    setReview({ status: "verified", note: "", duplicate_of: d.duplicate_flags?.[0]?.declaration_id || "" });
    setHashCheck(null);
    setSigUrl(null);
    if (d.signature?.file && !d.signature?.quarantined) {
      try {
        const r = await adminApi.get(`/admin/declarations/${d.id}/signature`, { responseType: "blob" });
        setSigUrl(URL.createObjectURL(r.data));
      } catch {}
    }
  };

  const doReview = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/declarations/${selected.id}/review`, {
        status: review.status,
        note: review.note || null,
        duplicate_of: review.status === "duplicate" ? review.duplicate_of || null : null,
      });
      toast.success(`Declaration marked ${review.status}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Review failed");
    } finally {
      setBusy(false);
    }
  };

  const recompute = async () => {
    try {
      const r = await adminApi.post(`/admin/declarations/${selected.id}/recompute-hash`);
      setHashCheck(r.data);
    } catch {
      toast.error("Recompute failed");
    }
  };

  const resolveIncident = async (id) => {
    try {
      await adminApi.patch(`/admin/incidents/${id}`, { status: "resolved" });
      toast.success("Incident resolved");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="max-w-6xl">
      <h1 className="font-heading font-bold text-2xl text-foreground mb-2" data-testid="admin-declarations-title">Declarations — private review queue</h1>
      <p className="text-xs text-muted-foreground mb-6">
        Declarations are private evidence. They are never visible to candidates or the public, and no totals are released while any election is open.
      </p>

      <Tabs defaultValue="queue">
        <TabsList>
          <TabsTrigger value="queue" data-testid="declarations-tab-queue">Review queue</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="declarations-tab-incidents">Upload incidents ({incidents.filter((i) => i.status === "open").length})</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-4">
          <Card className="mb-4"><CardContent className="p-4 flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs mb-1.5 block">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="w-44 bg-white" data-testid="declarations-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.keys(STATUS_BADGE).map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer pb-2">
              <Checkbox checked={filters.dupOnly} onCheckedChange={(v) => setFilters((f) => ({ ...f, dupOnly: !!v }))} data-testid="declarations-duplicates-filter" />
              <span className="text-sm">Duplicates only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer pb-2">
              <Checkbox checked={filters.includeTest} onCheckedChange={(v) => setFilters((f) => ({ ...f, includeTest: !!v }))} data-testid="declarations-include-test-filter" />
              <span className="text-sm">Include TEST data</span>
            </label>
          </CardContent></Card>

          <Card><CardContent className="p-0">
            <Table data-testid="admin-declarations-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Declarant</TableHead>
                  <TableHead>Flags</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {decls.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-8">No declarations match</TableCell></TableRow>
                )}
                {decls.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer" onClick={() => openDetail(d)} data-testid={`declaration-row-${d.declaration_number}`}>
                    <TableCell className="font-mono text-xs font-medium tabular-nums">{d.declaration_number}</TableCell>
                    <TableCell className="text-sm">{d.candidate_name}</TableCell>
                    <TableCell className="text-sm">{d.voter?.full_name}<p className="text-xs text-muted-foreground">{d.voter?.email}</p></TableCell>
                    <TableCell>
                      <div className="flex gap-1.5 flex-wrap">
                        {d.is_test && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[10px]">TEST</Badge>}
                        {(d.duplicate_flags || []).length > 0 && (
                          <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">
                            <AlertTriangle className="h-3 w-3 mr-1" /> dup ×{d.duplicate_flags.length}
                          </Badge>
                        )}
                        {d.signature?.quarantined && <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">quarantined</Badge>}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_BADGE[d.status]}>{d.status.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="font-mono text-xs tabular-nums">{d.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="incidents" className="mt-4">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Declaration</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground text-sm py-8">No upload incidents</TableCell></TableRow>
                )}
                {incidents.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono text-xs">{i.declaration_id}</TableCell>
                    <TableCell className="text-sm max-w-md">{i.reason}</TableCell>
                    <TableCell><Badge variant="outline" className={i.status === "open" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}>{i.status}</Badge></TableCell>
                    <TableCell>
                      {i.status === "open" && (
                        <Button size="sm" variant="outline" onClick={() => resolveIncident(i.id)} data-testid={`incident-resolve-${i.id}`}>Resolve</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading font-mono">{selected.declaration_number}</DialogTitle>
              </DialogHeader>
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline" className={STATUS_BADGE[selected.status]}>{selected.status}</Badge>
                {selected.is_test && <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300">TEST</Badge>}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <p><span className="text-muted-foreground">Candidate:</span> {selected.candidate_name}</p>
                <p><span className="text-muted-foreground">Ward:</span> {selected.ward || "—"}</p>
                <p><span className="text-muted-foreground">Declarant:</span> {selected.voter?.full_name}</p>
                <p><span className="text-muted-foreground">Email:</span> {selected.voter?.email}</p>
                <p className="sm:col-span-2"><span className="text-muted-foreground">Qualifying address:</span> {selected.voter?.qualifying_address}</p>
                <p><span className="text-muted-foreground">Voted:</span> {selected.voted_date} ({selected.vote_method})</p>
                <p><span className="text-muted-foreground">Confirmations:</span> {Object.values(selected.confirmations || {}).filter(Boolean).length}/{Object.keys(selected.confirmations || {}).length}</p>
                <p><span className="text-muted-foreground">Consents:</span> verification {selected.consents?.verification ? "✓" : "✗"}, retention {selected.consents?.evidence_retention ? "✓" : "✗"}</p>
                <p><span className="text-muted-foreground">Signature:</span> {selected.signature?.type}{selected.signature?.quarantined ? " (QUARANTINED)" : ""}</p>
              </div>
              {selected.signature?.type === "typed" && (
                <p className="text-xl italic border border-slate-200 rounded-lg p-3 bg-slate-50" style={{ fontFamily: "'Space Grotesk', cursive" }}>{selected.signature?.data}</p>
              )}
              {sigUrl && <img src={sigUrl} alt="Drawn signature" className="border border-slate-200 rounded-lg max-h-40 bg-white" data-testid="declaration-signature-image" />}

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">SHA-256 integrity hash</p>
                <p className="font-mono text-[11px] break-all">{selected.integrity_hash}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={recompute} data-testid="declaration-recompute-hash">
                    <Fingerprint className="h-3.5 w-3.5 mr-1.5" /> Recompute
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(selected.integrity_hash); toast.success("Hash copied"); }}>
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                  </Button>
                  {hashCheck && (
                    <Badge variant="outline" className={hashCheck.match ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-800 border-rose-200"} data-testid="declaration-hash-result">
                      {hashCheck.match ? "match — record intact" : "MISMATCH"}
                    </Badge>
                  )}
                </div>
              </div>

              {(selected.duplicate_flags || []).length > 0 && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
                  <p className="text-xs font-semibold text-rose-800 mb-1.5">Duplicate indicators</p>
                  <ul className="text-xs text-rose-900 space-y-1">
                    {selected.duplicate_flags.map((f, i) => (
                      <li key={i} className="font-mono">{f.declaration_number} — {f.reasons.join(", ")}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(selected.review_notes || []).length > 0 && (
                <div>
                  <p className="text-xs font-semibold mb-1.5">Review history</p>
                  <ul className="space-y-1.5">
                    {selected.review_notes.map((n, i) => (
                      <li key={i} className="text-xs bg-slate-50 border border-slate-200 rounded p-2">
                        {n.by} · {n.at?.slice(0, 16)} · {n.from_status} → {n.to_status}{n.note ? ` — ${n.note}` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Separator />
              <div className="space-y-3">
                <h3 className="font-heading font-semibold text-sm">Review action</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Select value={review.status} onValueChange={(v) => setReview((r) => ({ ...r, status: v }))}>
                    <SelectTrigger data-testid="declaration-review-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="under_review">Under review</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="duplicate">Duplicate</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  {review.status === "duplicate" && (
                    <Input placeholder="Original declaration ID" value={review.duplicate_of} onChange={(e) => setReview((r) => ({ ...r, duplicate_of: e.target.value }))} data-testid="declaration-duplicate-of" />
                  )}
                </div>
                <Textarea rows={2} placeholder="Reason / note (required for duplicate & rejected)" value={review.note} onChange={(e) => setReview((r) => ({ ...r, note: e.target.value }))} data-testid="declaration-review-note" />
                <Button onClick={doReview} disabled={busy} data-testid="declaration-review-submit">Apply review</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
